/**
 * Shared Status Color Palette
 *
 * Centralized color system for status indicators across the platform.
 * Used by Pill, StatsCard, and other status-related components.
 *
 * Ensures visual consistency for semantic states (success, error, warning, info, neutral).
 *
 * NOTE: This now uses the central color tokens from colors.ts
 */

import { colors } from './colors';

export type StatusVariant = 'success' | 'error' | 'warning' | 'info' | 'neutral';

/**
 * Status color definitions using central color tokens
 *
 * Each variant has:
 * - bg: Light background color for pills/badges
 * - iconBg: Slightly darker background for icon containers
 * - icon: Icon/primary color
 * - text: Text color (dark gray for readability on light backgrounds)
 */
export const statusColors = {
  success: {
    bg: colors.status.success.light,
    iconBg: colors.status.success.lighter,
    icon: colors.status.success.base,
    text: '#666666',  // Consistent text color across all variants
  },
  error: {
    bg: colors.status.error.light,
    iconBg: colors.status.error.lighter,
    icon: colors.status.error.base,
    text: '#666666',
  },
  warning: {
    bg: colors.status.warning.light,
    iconBg: colors.status.warning.lighter,
    icon: colors.status.warning.base,
    text: '#666666',
  },
  info: {
    bg: colors.status.info.light,
    iconBg: colors.status.info.lighter,
    icon: colors.status.info.base,
    text: '#666666',
  },
  neutral: {
    bg: colors.status.neutral.light,
    iconBg: colors.status.neutral.lighter,
    icon: colors.status.neutral.base,
    text: '#666666',
  },
} as const;

/**
 * Semantic meaning of each variant:
 *
 * - success: Healthy, active, online, verified, completed, positive states
 * - error: Failed, disconnected, invalid, critical issues, authentication errors
 * - warning: Pending, disabled, needs attention, caution, non-critical issues
 * - info: Processing, neutral information, default state, general notifications
 * - neutral: Disabled, inactive, off, unavailable, no action needed
 */

/**
 * Get colors for a specific variant
 *
 * @example
 * const colors = getStatusColors('success');
 * // { bg: '#EEFDF7', iconBg: '#ECFDF5', icon: '#10B981', text: '#666666' }
 */
function getStatusColors(variant: StatusVariant) {
  return statusColors[variant];
}
